import {
  AnalyticsQuery,
  AnalyticsQueryDatapointEnum,
  AnalyticsQueryQueryEnum,
  AnalyticsTimeTickEnum,
  Product
} from '@infura/packages.api'

import dayjs from 'lib/dayjs'
import { InfuraStats } from 'lib/stats/stats-types'

export const firstResultPath = ['data', 0, 'result']

export function makeStatQuery(
  query: AnalyticsQueryQueryEnum,
  datapoint: AnalyticsQueryDatapointEnum,
  time: string,
  userTimestamp: string,
  product: Product,
  projectIds: Array<string>
): AnalyticsQuery {
  return {
    datapoint,
    for: { public_key: projectIds },
    product,
    query,
    time: {
      from: time,
      tick: time.slice(-1)
    },
    userTimestamp
  } as any
}

interface StatsQueryParams {
  query: AnalyticsQueryQueryEnum
  datapoint: AnalyticsQueryDatapointEnum
  time: string
  userTimestamp: string
  product: Product
  projectIds: Array<string>
  method?: string
  network?: string
  intervals?: string
}

export function makeStat1_5Query({
  datapoint,
  intervals,
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  method = null,
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  network = null,
  product,
  projectIds,
  query,
  time,
  userTimestamp
}: StatsQueryParams): AnalyticsQuery {
  let tick: string = time.slice(-1)
  if (time === '1h') tick = 'm'
  const statsQuery = {
    datapoint,
    product,
    query,
    time: intervals
      ? {
          intervals
        }
      : {
          from: time,
          tick: tick
        },

    userTimestamp
  }
  if (projectIds.length === 1) statsQuery['for'] = { public_key: projectIds }
  if (method != null) statsQuery['method'] = method as any
  if (network != null) statsQuery['network'] = network as any

  return statsQuery as any
}

export function getDateString(ts, periodType: InfuraStats.AnalyticsDurations) {
  let format = 'MMM D'
  if (periodType === InfuraStats.AnalyticsDurations.LAST_15_MINUTES) format = 'H:mm'
  if (periodType === InfuraStats.AnalyticsDurations.LAST_1_HOUR) format = 'H:mm'
  if (periodType === InfuraStats.AnalyticsDurations.LAST_24_HOURS) format = 'H:mm'
  if (periodType === InfuraStats.AnalyticsDurations.LAST_7_DAYS) format = 'MMM D H:mm'
  if (periodType === InfuraStats.AnalyticsDurations.LAST_30_DAYS) format = 'MMM D'
  return dayjs(ts).format(format)
}

export function getDiffPercentage(p: number, c: number): number {
  const absDiff = Math.abs(p - c)

  if (p === 0 || c === 0) return 0

  // percentage decrease from previous amount
  if (p > c) {
    return -((absDiff / p) * 100).toFixed(2)
  }

  // percentage increase from previous amount
  return +((absDiff / c) * 100).toFixed(2)
}

export interface MetaQueries {
  type?: string
  name?: string
  queries: Array<Omit<AnalyticsQuery, 'product'>>
}

export function durationToAPIPeriod(duration: InfuraStats.AnalyticsDurations): InfuraStats.AnalyticsPeriod {
  if (duration === InfuraStats.AnalyticsDurations.LAST_30_DAYS) return '30d'
  if (duration === InfuraStats.AnalyticsDurations.LAST_7_DAYS) return '7d'
  if (duration === InfuraStats.AnalyticsDurations.LAST_24_HOURS) return '24h'
  if (duration === InfuraStats.AnalyticsDurations.LAST_1_HOUR) return '1h'
  if (duration === InfuraStats.AnalyticsDurations.LAST_15_MINUTES) return '15m'
  return '7d'
}

export function periodToDuration(period: InfuraStats.AnalyticsPeriod): InfuraStats.AnalyticsDurations {
  if (period === '30d') return InfuraStats.AnalyticsDurations.LAST_30_DAYS
  if (period === '7d') return InfuraStats.AnalyticsDurations.LAST_7_DAYS
  if (period === '24h') return InfuraStats.AnalyticsDurations.LAST_24_HOURS
  if (period === '1h') return InfuraStats.AnalyticsDurations.LAST_1_HOUR
  if (period === '15m') return InfuraStats.AnalyticsDurations.LAST_15_MINUTES
  return InfuraStats.AnalyticsDurations.LAST_7_DAYS
}

export enum AnalyticsQueryKeys {
  CREDIT_USAGE_TODAY = 'CREDIT_USAGE_TODAY',
  REQUESTS_LAST_15_MINUTES = 'REQUESTS_LAST_15_MINUTES',
  REQUESTS_LAST_1_HOUR = 'REQUESTS_LAST_1_HOUR',
  REQUESTS_LAST_DAY = 'REQUESTS_LAST_DAY',
  REQUESTS_LAST_7_DAYS = 'REQUESTS_LAST_7_DAYS',
  REQUESTS_LAST_14_DAYS = 'REQUESTS_LAST_14_DAYS',
  REQUESTS_LAST_30_DAYS = 'REQUESTS_LAST_30_DAYS',
  REQUESTS_TODAY = 'REQUESTS_TODAY',
  REQUESTS_UTC_TODAY = 'REQUESTS_UTC_TODAY',
  REQUESTS_CUSTOM_TIMEFRAME = 'REQUESTS_CUSTOM_TIMEFRAME',
  REQUESTS_TOTAL_LAST_30_DAYS = 'REQUESTS_TOTAL_LAST_30_DAYS'
}

export const statsUtils: { [key in AnalyticsQueryKeys]: MetaQueries } = {
  [AnalyticsQueryKeys.REQUESTS_LAST_15_MINUTES]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TOTAL,
        time: {
          from: `${dayjs.utc().format('H')}h`, // Get the current utc hour
          tick: AnalyticsTimeTickEnum.M
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_LAST_1_HOUR]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TOTAL,
        time: {
          from: '1h',
          tick: AnalyticsTimeTickEnum.M
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_UTC_TODAY]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TOTAL,
        time: { intervals: `${dayjs.utc().startOf('day').toISOString()}/${dayjs.utc().endOf('day').toISOString()}` },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_TODAY]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TOTAL,
        time: {
          from: `${dayjs.utc().format('H')}h`, // Get the current utc hour
          tick: AnalyticsTimeTickEnum.H
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.CREDIT_USAGE_TODAY]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.CREDITS,
        query: AnalyticsQueryQueryEnum.TOTAL,
        time: {
          from: `${dayjs.utc().format('H')}h`, // Get the current utc hour
          tick: AnalyticsTimeTickEnum.H
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_LAST_DAY]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TIMESERIES,
        time: {
          from: '24h',
          tick: AnalyticsTimeTickEnum.H
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_LAST_7_DAYS]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TIMESERIES,
        time: {
          from: '7d',
          tick: AnalyticsTimeTickEnum.D
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_LAST_14_DAYS]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TIMESERIES,
        time: {
          from: '14d',
          tick: AnalyticsTimeTickEnum.D
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_LAST_30_DAYS]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TIMESERIES,
        time: {
          from: '30d',
          tick: AnalyticsTimeTickEnum.D
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  [AnalyticsQueryKeys.REQUESTS_TOTAL_LAST_30_DAYS]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TOTAL,
        time: {
          from: '30d',
          tick: AnalyticsTimeTickEnum.D
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  },
  // need to add intervals
  [AnalyticsQueryKeys.REQUESTS_CUSTOM_TIMEFRAME]: {
    queries: [
      {
        datapoint: AnalyticsQueryDatapointEnum.COUNT,
        query: AnalyticsQueryQueryEnum.TIMESERIES,
        time: {
          from: '1d',
          tick: AnalyticsTimeTickEnum.D
        },
        userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
      }
    ]
  }
}

export const StatsSelectOptions = [
  {
    icon: 'calendar-alt',
    label: InfuraStats.AnalyticsDurations.LAST_15_MINUTES,
    value: InfuraStats.AnalyticsDurations.LAST_15_MINUTES
  },
  {
    icon: 'calendar-alt',
    label: InfuraStats.AnalyticsDurations.LAST_1_HOUR,
    value: InfuraStats.AnalyticsDurations.LAST_1_HOUR
  },
  {
    icon: 'calendar-alt',
    label: InfuraStats.AnalyticsDurations.LAST_24_HOURS,
    value: InfuraStats.AnalyticsDurations.LAST_24_HOURS
  },
  {
    icon: 'calendar-alt',
    label: InfuraStats.AnalyticsDurations.LAST_7_DAYS,
    value: InfuraStats.AnalyticsDurations.LAST_7_DAYS
  },
  {
    icon: 'calendar-alt',
    label: InfuraStats.AnalyticsDurations.LAST_30_DAYS,
    value: InfuraStats.AnalyticsDurations.LAST_30_DAYS
  }
]

export default statsUtils
